import { HUBSPOT_HUB_ID } from 'config';
import { initHubSpotDraggable } from './draggable';

declare global {
  interface Window {
    // Queue of callbacks HubSpot invokes once Conversations is ready.
    hsConversationsOnReady?: Array<() => void>;
    // HubSpot tracking-code queue (identify, trackPageView, etc) - feeds
    // Analytics (contact timeline, page views), NOT the Conversations widget.
    _hsq?: unknown[][];
    // The Conversations widget's own visitor identity, read on load.
    hsConversationsSettings?: {
      identificationEmail?: string;
      loadImmediately?: boolean;
    };
    // Conversations widget API, present once the script has loaded.
    HubSpotConversations?: {
      clear?: (options?: { resetWidget?: boolean }) => void;
      widget?: {
        load?: (options?: { widgetOpen?: boolean }) => void;
      };
    };
  }
}

const SCRIPT_ID = 'hs-script-loader';
const IS_MOCK_HUB_ID = HUBSPOT_HUB_ID === 'mock';

// Tracks the last email identified so a re-render with the same logged-in
// user doesn't re-identify/reset on every call.
let lastIdentifiedEmail: string | undefined;

/**
 * Identifies the visitor so Conversations associates chats/tickets with the
 * logged-in user's HubSpot Contact instead of an anonymous visitor (or a
 * previously logged-in one).
 *
 * `_hsq.push(['identify', ...])` only feeds HubSpot Analytics - the
 * Conversations widget reads its own visitor identity from
 * `hsConversationsSettings`, set here too. This runs on every call,
 * independent of whether the widget script has already been injected:
 * login, switch-company, and logout all navigate via React Router (no full
 * page reload — see `useLoginWithNavigation`), so the script tag from a
 * previous user's session is still in the DOM.
 */
const identifyVisitor = (email?: string): void => {
  if (!email || email === lastIdentifiedEmail) {
    return;
  }
  const isReidentifying = lastIdentifiedEmail !== undefined;
  lastIdentifiedEmail = email;

  window._hsq = window._hsq || [];
  window._hsq.push(['identify', { email }]);

  window.hsConversationsSettings = {
    ...window.hsConversationsSettings,
    identificationEmail: email,
    loadImmediately: true,
  };

  // A different user is now identified in this tab. HubSpot's own visitor
  // cookie already ties this browser to the previous user's Contact, so
  // setting hsConversationsSettings alone won't detach an already-running
  // widget from it - it has to be fully reset (clears the chat cookies) and
  // reloaded with the new identity. The script tag being present doesn't
  // guarantee HubSpotConversations has finished initializing yet, so queue
  // the reset via hsConversationsOnReady rather than calling the API
  // directly - HubSpot fires that queue immediately if already ready, or
  // once it becomes ready otherwise, so the reset is never silently missed.
  if (isReidentifying && document.getElementById(SCRIPT_ID)) {
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
      window.HubSpotConversations?.clear?.({ resetWidget: true });
      window.HubSpotConversations?.widget?.load?.();
    });
  }
};

/**
 * Loads the HubSpot Customer Agent widget and makes it draggable.
 *
 * Gated entirely by the `VITE_HUBSPOT_HUB_ID` env var, which is only set for the
 * tenants that should show the widget (my.delogue / es.delogue / silo7). On every
 * other tenant/env the var is empty and this is a no-op. Idempotent — calling it
 * more than once injects the script only once.
 */
export const loadHubSpot = (isDesigner = false, email?: string): void => {
  if (!isDesigner) {
    return;
  }
  if (!HUBSPOT_HUB_ID || IS_MOCK_HUB_ID) {
    return;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  identifyVisitor(email);

  if (document.getElementById(SCRIPT_ID)) {
    return;
  }

  // Register the drag-handle initialiser before the script loads so HubSpot
  // runs it as soon as Conversations is ready.
  window.hsConversationsOnReady = window.hsConversationsOnReady || [];
  window.hsConversationsOnReady.push(initHubSpotDraggable);

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = `https://js.hs-scripts.com/${HUBSPOT_HUB_ID}.js`;
  document.body.appendChild(script);
};
